import * as React from 'react';
import ReactDOM from 'react-dom';

import { throttle } from 'lodash';
import type videojs from 'video.js';

import store from 'js/lib/coursera.store';
import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import user from 'js/lib/user';

import { getFlexDefaults } from 'pages/open-course/video/util/video';

import { VIDEO_RESOLUTIONS } from 'bundles/asset-admin/constants/AssetTypes';
import {
  dataAttributeToSubtitleTracks,
  subtitleTracksToVideoPlayerSubtitlesVttAttribute,
} from 'bundles/cml/shared/components/asset/assetUtils';
import type { Html5VideoResolutionsSources } from 'bundles/phoenix/components/HTML5Video';
import { loadVideoJS } from 'bundles/phoenix/utils/videoSetupUtils';
import type { Video, VideoSourcesByResolution } from 'bundles/phoenix/utils/videoSetupUtils';
import userPreferences from 'bundles/video-player/utils/userPreferences';

import 'css!./__styles__/CMLVideoJSV2';
import 'css!pages/open-course/video/styl/video';
// eslint-disable-next-line
import 'css!video.js/dist/video-js.css';

const loadVideoJSPlayer = () => import('pages/open-course/video/util/video');

type Props = {
  htmlContent?: string | null;
  children: JSX.Element;
};

/**
 * Wrapper that replaces all child video asset nodes with an interactive video player via videoJS
 * This is essentially the same as CMLVideoJS but it just fetches the video data already available in the html
 * rather than making another API call to fetch it, thanks to unified rendering
 * */
class CMLVideoJSV2 extends React.Component<Props> {
  willUnmount = false;

  componentDidMount() {
    this.maybeRenderVideoJS();
  }

  maybeRenderVideoJS() {
    const { htmlContent } = this.props;
    if (!htmlContent || htmlContent.includes('cml-asset-video')) {
      this.renderVideoJS();
    }
  }

  renderVideoJS = async () => {
    const VideoJSPlayer = (await loadVideoJSPlayer()).default;
    if (this.willUnmount) {
      return;
    }

    // eslint-disable-next-line react/no-find-dom-node
    const dom = ReactDOM.findDOMNode(this);
    const videoAssetNodes = (dom as Element)?.querySelectorAll<HTMLElement>('.cml-asset-video') || [];

    // for each found asset node, fetch the video data from its attributes and render a videoJS player
    videoAssetNodes.forEach((assetEl: HTMLElement) => {
      // @ts-expect-error TSMIGRATION
      const videoSourcesByResolution: VideoSourcesByResolution = {};
      // @ts-expect-error TSMIGRATION
      const videoSources: Html5VideoResolutionsSources = {};
      // build the video data in the format required by videoJS for `loadVideoJS()` below
      VIDEO_RESOLUTIONS.forEach((resolution) => {
        // When a new video asset is first uploaded, we don't immediately have sources by resolution available,
        // and thus we need this `fallbackUrl` temporarily to render the video correctly on new uploads.
        // `previewImageUrl` will be unavailable as well but we don't have a good fallback for that right now.
        const fallbackUrl = assetEl.getAttribute('data-url') || '';
        const mp4VideoUrl = assetEl.getAttribute(`data-resolution-${resolution}-mp4`) || fallbackUrl;
        const webMVideoUrl = assetEl.getAttribute(`data-resolution-${resolution}-webm`) || fallbackUrl;
        const previewImageUrl = assetEl.getAttribute(`data-preview-thumbnail-${resolution}`) || '';
        videoSourcesByResolution[resolution] = { previewImageUrl, mp4VideoUrl, webMVideoUrl };
        videoSources[resolution] = [
          {
            src: mp4VideoUrl,
            type: 'video/mp4',
          },

          {
            src: webMVideoUrl,
            type: 'video/webm',
          },
        ];
      });

      const usedId = user.get()?.id?.toString();
      const assetId = assetEl.getAttribute('data-id') || '';
      const autoSaveProgressId = tupleToStringKey([usedId, assetId]);
      const dataSubtitleTracks = assetEl.getAttribute('data-subtitle-tracks');
      const subtitlesVtt = subtitleTracksToVideoPlayerSubtitlesVttAttribute(
        dataAttributeToSubtitleTracks(dataSubtitleTracks)
      );

      const video: Video = {
        sources: { byResolution: videoSourcesByResolution },
        ...(subtitlesVtt ? { subtitlesVtt } : {}),
        defaultSubtitleLanguage: userPreferences.get('subtitleLanguage') || 'off',
      };

      const playerOptions: videojs.PlayerOptions = {
        children: getFlexDefaults({
          // At this point, if subtitles are present, they were in data attributes, which we only set if subtitles were already enabled
          videoSubtitlesDisabled: subtitlesVtt === undefined,
          defaultSubtitleLanguage: video.defaultSubtitleLanguage,
        }).children,
      };
      const videoJs = loadVideoJS(VideoJSPlayer, video, videoSources, {
        playerOptions,
      });
      const applyUserPreferences = () => {
        const playbackRate = userPreferences.get('playbackRate') || 1;
        videoJs.playbackRate(playbackRate);
      };
      const handleChangePlaybackRate = () => {
        userPreferences.set('playbackRate', videoJs.playbackRate());
      };
      const handleChangeSubtitles = (event: Event, track: TextTrack) => {
        userPreferences.set('subtitleLanguage', track.language);
      };
      const restoreInitialProgress = () => {
        const videoProgress = store.get(autoSaveProgressId);
        if (!videoProgress) return;
        // Adding the 'vjs-has-started' class hides the poster image and shows the video controls
        videoJs.addClass('vjs-has-started');
        videoJs.currentTime(videoProgress);
      };
      const saveProgress = () => {
        const videoProgress = videoJs.currentTime();
        const videoDuration = videoJs.duration();
        if (videoProgress <= 0 || videoProgress >= videoDuration) return;
        store.set(autoSaveProgressId, videoProgress);
      };
      const removeProgress = () => {
        store.remove(autoSaveProgressId);
      };
      const handleLoadMetadata = () => {
        applyUserPreferences();
        restoreInitialProgress();
      };
      videoJs.on(
        'subtitleschange',
        // TS definition expects a function with one argument, but testing this we clearly have 2
        // @ts-expect-error
        handleChangeSubtitles
      );
      videoJs.on('ratechange', handleChangePlaybackRate);
      videoJs.on('loadedmetadata', handleLoadMetadata);

      videoJs.on('seeked', saveProgress);
      videoJs.on('timeupdate', throttle(saveProgress, 2000, { leading: false }));
      videoJs.on('ended', removeProgress);

      assetEl.innerHTML = ''; // eslint-disable-line no-param-reassign
      assetEl.appendChild(videoJs.el());
    });
  };

  shouldComponentUpdate() {
    return false;
  }

  componentWillUnmount() {
    this.willUnmount = true;
  }

  render() {
    return this.props.children;
  }
}

export default CMLVideoJSV2;
